<template>
  <Fragment>
    <b-card body-class="p-3" class="mb-0">
      <div class="my-3 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <h5 class="mb-0 font-weight-bold">Bordereaux Status Pending</h5>

          <div class="ml-3 d-flex align-items-center" style="gap: 8px;">
            <BDXStatusPendingManagerSaveButton @success="onSaveButtonSuccess" />
          </div>
        </div>

        <div class="d-flex align-items-center">
          <BaseSearchInput v-model="search" />

          <BDXStatusPendingManagerExportButton />

          <HelperIcon>
            <div class="m-0 font-italic">
              A status of Pending in BDX Status reflects any BDX that is currently
              marked 'Active' within LimeIQ but has been submitted in the most
              recent report period that may be inactive. This ctriteria includes a
              BDX (grouped by the LimeIQ Key) where all claims reflect a closed
              status, there are no pending reserves, and no payments were made
              during the report period. These must be reviewed by the DCA to
              decide if the BDX is to be marked inactive or remain active
              depending on the circumstances. Once marked inactive, a final
              closing BDX will be exported. BDX files are restricted from being
              exported if there are any BDX Status items left in Pending status.
            </div>
          </HelperIcon>
        </div>
      </div>

      <div v-if="activeColumnFilters.length" class="mb-2 d-flex align-items-center flex-wrap active-filters">
        <b-badge
          v-for="filter in activeColumnFilters"
          :key="filter.key"
          class="text-primary border border-primary filter-badge"
          pill
        >
          {{ filter.label }}: {{ filter.value }}
          <a
            tabindex="0"
            role="button"
            class="text-danger ml-1"
            @click.prevent="setColumnFilter(filter.key, '')"
          >
            <b-icon icon="x" :scale="1.5"></b-icon>
          </a>
        </b-badge>
      </div>

      <BDXStatusPendingManagerList
        ref="listRef"
        v-model="bdxStatusList"
        :active-preview-id="previewItem ? previewItem.id : null"
        @preview:item="onPreviewItem"
      />
    </b-card>

    <BDXStatusPendingPreviewModal
      v-model="showPreviewModal"
      :item="previewItem"
    />
  </Fragment>
</template>

<script>
import { defineComponent, ref, computed, provide, watch } from '@vue/composition-api'

const STATUS_COLUMN_LABELS = {
  umr: 'UMR',
  risk_code: 'Risk Code',
  cat_code: 'Cat Code',
  section_no: 'Section #',
  lead_underwriter: 'Lead Underwriter'
}
import { Fragment } from 'vue-fragment'

import { BaseSearchInput } from '@components/base'
import { HelperIcon } from '@components/misc'

import { BDXStatusPendingManagerInjectionKey } from './BDXStatusPendingManager.constants'
import BDXStatusPendingManagerSaveButton from './BDXStatusPendingManagerSaveButton.vue'
import BDXStatusPendingManagerExportButton from './BDXStatusPendingManagerExportButton.vue'
import BDXStatusPendingManagerList from './BDXStatusPendingManagerList.vue'
import BDXStatusPendingPreviewModal from './BDXStatusPendingPreviewModal.vue'

export default defineComponent({
  name: 'BDXStatusPendingManager',
  components: {
    Fragment,

    BaseSearchInput,

    HelperIcon,

    BDXStatusPendingManagerSaveButton,
    BDXStatusPendingManagerExportButton,
    BDXStatusPendingManagerList,
    BDXStatusPendingPreviewModal
  },
  setup() {
    const listRef = ref(null)

    const search = ref('')

    const columnFilters = ref({})

    const setColumnFilter = (key, value) => {
      columnFilters.value = { ...columnFilters.value, [key]: value || undefined }
    }

    const activeColumnFilters = computed(() =>
      Object.entries(columnFilters.value)
        .filter(([, v]) => !!v)
        .map(([key, value]) => ({ key, label: STATUS_COLUMN_LABELS[key] || key, value }))
    )

    const bdxStatusList = ref([])

    // Preview modal
    const showPreviewModal = ref(false)
    const previewItem = ref(null)

    const onPreviewItem = (item) => {
      if (previewItem.value && previewItem.value.id === item.id && showPreviewModal.value) {
        showPreviewModal.value = false
        return
      }
      previewItem.value = item
      showPreviewModal.value = true
    }

    watch(showPreviewModal, (val) => {
      if (!val) previewItem.value = null
    })

    // Save button
    const onSaveButtonSuccess = async () => {
      if (!!listRef.value) await listRef.value.fetchItems()

      bdxStatusList.value = []
    }

    provide(BDXStatusPendingManagerInjectionKey, {
      search,
      columnFilters,
      setColumnFilter,

      bdxStatusList
    })

    return {
      listRef,

      search,
      columnFilters,
      setColumnFilter,
      activeColumnFilters,

      bdxStatusList,

      showPreviewModal,
      previewItem,
      onPreviewItem,

      onSaveButtonSuccess
    }
  }
})
</script>

<style scoped>
>>> .base-search-input {
  max-width: 300px;
}

.active-filters {
  gap: 8px;
}

.active-filters >>> .filter-badge {
  padding: 4px 16px;
  font-size: 12px;
  font-weight: 600;
}
</style>
